import { useEffect, useState } from "react";
import { Image as ImageIcon } from "@phosphor-icons/react";
import { API } from "@/lib/apiClient";

/**
 * <AuthenticatedImage src={value} /> transparently handles THREE kinds of value:
 *   1. Empty / falsy → renders a soft placeholder.
 *   2. "data:image/..." (legacy inline base64) → renders directly.
 *   3. "photo:<uuid>" (new object storage ref) → appends ?token=<jwt> and renders.
 *
 * The query-param token lets us use the native <img> element (which cannot send
 * Authorization headers) without exposing an unauth download endpoint.
 * Note: the token is scoped to this user's session and already lives in
 * localStorage; appending it to an image URL is no less secure than the
 * existing API usage.
 */
export default function AuthenticatedImage({ src, alt = "", className = "", onClick }) {
  const [tick, setTick] = useState(0);

  // Re-render when token refreshes (login/logout) so stale URLs don't persist.
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === "pms_token") setTick((t) => t + 1);
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  if (!src) {
    return (
      <div className={`flex items-center justify-center bg-slate-100 text-slate-300 ${className}`}>
        <ImageIcon size={32} weight="duotone" />
      </div>
    );
  }

  if (typeof src === "string" && src.startsWith("photo:")) {
    const token = typeof window !== "undefined" ? localStorage.getItem("pms_token") : "";
    const qs = token ? `?token=${encodeURIComponent(token)}&_=${tick}` : `?_=${tick}`;
    const url = `${API}/photos/${encodeURIComponent(src)}${qs}`;
    return <img src={url} alt={alt} className={className} onClick={onClick} loading="lazy" />;
  }

  // Legacy data URI or any other direct image source.
  return <img src={src} alt={alt} className={className} onClick={onClick} loading="lazy" />;
}
